import { Input, InputNumber, Radio, Select, Space, Typography } from 'antd';
import type { ScreenDiagnostic, ScreenVocabulary } from '@/api/screens';
import { errorAt } from './diagnostics';
import { LocalizedField } from './LocalizedField';
import type { LocalizedText, ScreenDef } from './model';

const { Text } = Typography;

const Label = ({ children }: { children: string }) => (
  <div style={{ fontSize: 12, marginBottom: 4 }}>{children}</div>
);

const options = (values: string[]) => values.map((value) => ({ value, label: value }));

/**
 * What each kind is configured with: its `spec` (DataroomKindSpecs), and for UPLOAD its evidence kind and
 * file types. Dropdowns are the server's lists; a choice the gate refuses comes back on the control.
 */
export function KindSpecEditor({
  screen,
  path,
  vocabulary,
  locales,
  diagnostics,
  onChange,
}: {
  screen: ScreenDef;
  path: string;
  vocabulary: ScreenVocabulary;
  locales: string[];
  diagnostics: ScreenDiagnostic[];
  onChange: (updates: Partial<ScreenDef>) => void;
}) {
  const spec = (screen.spec ?? {}) as Record<string, unknown>;
  const specPath = `${path}/spec`;
  const bad = (field: string) => (errorAt(diagnostics, `${specPath}/${field}`) ? 'error' : undefined);
  const set = (field: string, value: unknown) => {
    const next = { ...spec, [field]: value };
    if (value === undefined || value === null) delete next[field];
    onChange({ spec: next });
  };
  const enumField = (field: string, label: string, values: string[]) => (
    <div style={{ width: 260 }}>
      <Label>{label}</Label>
      <Select
        style={{ width: '100%' }}
        aria-label={label}
        data-testid={`spec-${field}`}
        value={spec[field] as string | undefined}
        status={bad(field)}
        options={options(values)}
        onChange={(value) => set(field, value)}
      />
    </div>
  );

  switch (screen.kind) {
    case 'UPLOAD':
      return (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ width: 260 }}>
            <Label>Evidence kind</Label>
            <Select
              style={{ width: '100%' }}
              allowClear
              aria-label="Evidence kind"
              placeholder="Pick the evidence type"
              value={screen.evidenceKind}
              status={errorAt(diagnostics, `${path}/evidenceKind`) ? 'error' : undefined}
              options={options(vocabulary.evidenceKinds)}
              onChange={(evidenceKind) => onChange({ evidenceKind })}
            />
          </div>
          <div style={{ width: 340 }}>
            <Label>Accepted file types (empty: all this hub accepts)</Label>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              aria-label="Accepted file types"
              value={screen.ui?.accept ?? []}
              status={errorAt(diagnostics, `${path}/ui/accept`) ? 'error' : undefined}
              options={options(vocabulary.uploadMediaTypes)}
              onChange={(accept: string[]) => {
                const ui = { ...(screen.ui ?? {}) };
                if (accept.length > 0) ui.accept = accept;
                else delete ui.accept;
                onChange({ ui: Object.keys(ui).length > 0 ? ui : undefined });
              }}
            />
          </div>
        </div>
      );
    case 'DECLARATION':
    case 'AADHAAR_OTP': {
      const usesKey = spec.textKey !== undefined;
      return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <div style={{ width: 200 }}>
            <Label>Text version</Label>
            <Input
              className="ol-mono"
              aria-label="Text version"
              value={(spec.version as string) ?? ''}
              status={bad('version')}
              onChange={(e) => set('version', e.target.value)}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              The consent record names this. Change it whenever the text changes.
            </Text>
          </div>
          <Radio.Group
            value={usesKey ? 'key' : 'text'}
            aria-label="Where the text comes from"
            onChange={(e) => {
              const { text: _text, textKey: _key, translations: _translations, ...rest } = spec;
              onChange({ spec: { ...rest, ...(e.target.value === 'key' ? { textKey: '' } : { text: '' }) } });
            }}
          >
            <Radio value="text">Write it here</Radio>
            <Radio value="key">Content key (per language)</Radio>
          </Radio.Group>
          {usesKey ? (
            <Input
              className="ol-mono"
              aria-label="Declaration content key"
              value={(spec.textKey as string) ?? ''}
              status={bad('textKey')}
              onChange={(e) => set('textKey', e.target.value)}
            />
          ) : (
            <Input.TextArea
              aria-label="Declaration text"
              autoSize={{ minRows: 3, maxRows: 10 }}
              value={(spec.text as string) ?? ''}
              status={bad('text')}
              onChange={(e) => set('text', e.target.value)}
            />
          )}
          {!usesKey &&
            screen.kind === 'DECLARATION' &&
            locales
              .filter((l) => l !== 'en')
              .map((locale) => {
                const translations = (spec.translations ?? {}) as Record<string, string>;
                return (
                  <div key={locale}>
                    <Label>{`Declaration text (${locale})`}</Label>
                    <Input.TextArea
                      aria-label={`Declaration text (${locale})`}
                      dir={locale === 'ar' ? 'rtl' : undefined}
                      autoSize={{ minRows: 3, maxRows: 10 }}
                      value={translations[locale] ?? ''}
                      status={bad(`translations/${locale}`)}
                      onChange={(e) => {
                        const next = { ...translations, [locale]: e.target.value };
                        if (!e.target.value) delete next[locale];
                        set('translations', Object.keys(next).length > 0 ? next : undefined);
                      }}
                    />
                  </div>
                );
              })}
        </Space>
      );
    }
    case 'PARTIES':
      return (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ width: 280 }}>
            <Label>Roles the applicant can add</Label>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              aria-label="Roles"
              value={(spec.roles as string[]) ?? []}
              status={bad('roles')}
              options={options(vocabulary.partyRoles)}
              onChange={(roles: string[]) => set('roles', roles)}
            />
          </div>
          <div style={{ width: 280 }}>
            <Label>Ownership % is required for</Label>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              aria-label="Ownership required for"
              value={(spec.requireOwnershipFor as string[]) ?? []}
              status={bad('requireOwnershipFor')}
              options={options(vocabulary.partyRoles)}
              onChange={(roles: string[]) => set('requireOwnershipFor', roles.length ? roles : undefined)}
            />
          </div>
          <div style={{ width: 280 }}>
            <Label>A DIN is required for</Label>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              aria-label="DIN required for"
              value={(spec.requireDinFor as string[]) ?? []}
              status={bad('requireDinFor')}
              options={options(vocabulary.partyRoles)}
              onChange={(roles: string[]) => set('requireDinFor', roles.length ? roles : undefined)}
            />
          </div>
          <div>
            <Label>Fewest</Label>
            <InputNumber
              aria-label="Fewest parties"
              value={spec.minParties as number | undefined}
              onChange={(v) => set('minParties', v ?? undefined)}
              status={errorAt(diagnostics, specPath) ? 'error' : undefined}
            />
          </div>
          <div>
            <Label>Most</Label>
            <InputNumber
              aria-label="Most parties"
              value={spec.maxParties as number | undefined}
              onChange={(v) => set('maxParties', v ?? undefined)}
              status={errorAt(diagnostics, specPath) ? 'error' : undefined}
            />
          </div>
        </div>
      );
    case 'CAPTURE': {
      const prompts = screen.ui?.prompts ?? [];
      const setPrompts = (next: LocalizedText[]) => {
        const ui = { ...(screen.ui ?? {}) };
        if (next.length > 0) ui.prompts = next;
        else delete ui.prompts;
        onChange({ ui: Object.keys(ui).length > 0 ? ui : undefined });
      };
      return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            {enumField('mode', 'What to capture', vocabulary.captureModes)}
            {spec.mode === 'VIDEO' && (
              <div>
                <Label>{`Longest video, seconds (${vocabulary.limits.videoMinSeconds} to ${vocabulary.limits.videoMaxSeconds})`}</Label>
                <InputNumber
                  aria-label="Longest video in seconds"
                  value={spec.maxSeconds as number | undefined}
                  status={bad('maxSeconds')}
                  onChange={(v) => set('maxSeconds', v ?? undefined)}
                />
              </div>
            )}
          </div>
          {spec.mode === 'VIDEO' && (
            <>
              {prompts.map((prompt, i) => (
                <LocalizedField
                  key={i}
                  label={`Prompt ${i + 1}`}
                  value={prompt}
                  locales={locales}
                  diagnostics={diagnostics}
                  path={`${path}/ui/prompts/${i}`}
                  onChange={(next) =>
                    setPrompts(next ? prompts.map((p, j) => (j === i ? next : p)) : prompts.filter((_, j) => j !== i))
                  }
                />
              ))}
              {prompts.length < (vocabulary.limits.maxPrompts ?? 6) && (
                <a role="button" onClick={() => setPrompts([...prompts, { en: 'Look at the camera' }])}>
                  Add a prompt
                </a>
              )}
            </>
          )}
        </Space>
      );
    }
    case 'OTP':
      return enumField('channel', 'Send the code by', vocabulary.otpChannels);
    case 'IDENTIFIER':
      return enumField('scheme', 'Which number', vocabulary.identifierSchemes);
    case 'BANK_DETAILS':
      return enumField('mode', 'Ask for', vocabulary.bankModes);
    default:
      return null;
  }
}
